import React from "react";
import PropTypes from "prop-types";
import DialogTitle from "@material-ui/core/DialogTitle";
import { makeStyles } from "@material-ui/core/styles";
import Close from "@material-ui/icons/Close";
import useThemeColors from "../../hooks/useThemeColors";

const HEADER_BG = "#353534";

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: (props) => (props.variant === "drawer" ? "10px 14px" : "8px 12px"),
    background: HEADER_BG,
    color: "#fff",
    flexShrink: 0,
    boxSizing: "border-box",
    borderRadius: (props) =>
      props.variant === "drawer" ? "10px 10px 0 0" : "6px 6px 0 0",
    overflow: "hidden",
    // Legacy modal papers use overflow: visible; this hides the 1px
    // antialias seam that can appear above the dark header.
    marginTop: "-1px",
  },
  strip: {
    width: 4,
    height: (props) => (props.variant === "drawer" ? 38 : 30),
    borderRadius: 2,
    background: (props) => props.clientColor,
    flex: "0 0 auto",
  },
  main: {
    minWidth: 0,
    flex: 1,
  },
  title: {
    margin: 0,
    color: "#fff",
    fontSize: (props) => (props.variant === "drawer" ? 18 : 16),
    fontWeight: 500,
    lineHeight: 1.25,
    letterSpacing: "-0.01em",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  subtitle: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginTop: 1,
    color: "rgba(255,255,255,0.72)",
    fontSize: 12,
    lineHeight: 1.35,
    minWidth: 0,
    flexWrap: "wrap",
    "& strong": {
      color: "#fff",
      fontWeight: 500,
    },
  },
  sep: {
    width: 3,
    height: 3,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.45)",
    flex: "0 0 auto",
  },
  controls: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 11px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.12)",
    color: "#fff",
    fontSize: 11,
    fontWeight: 500,
    lineHeight: 1.2,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  pillDot: {
    "&::before": {
      content: '""',
      width: 6,
      height: 6,
      borderRadius: "50%",
      background: "currentColor",
      flex: "0 0 auto",
    },
  },
  pillOk: {
    background: "rgba(76, 175, 80, 0.12)",
    color: "#2e7d32",
  },
  pillWarning: {
    background: "rgba(255, 152, 0, 0.14)",
    color: "#b45c00",
  },
  pillError: {
    background: "rgba(244, 67, 54, 0.1)",
    color: "#d6342d",
  },
  pillInfo: {
    background: "rgba(21, 101, 192, 0.14)",
    color: "#90caf9",
  },
  closeButton: {
    color: "#fff",
    cursor: "pointer",
    padding: 4,
    marginLeft: 0,
    borderRadius: 4,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "none",
    fontFamily: "inherit",
    flexShrink: 0,
    "&:hover:not(:disabled)": { background: "rgba(255,255,255,0.12)" },
    "&:disabled": {
      opacity: 0.45,
      cursor: "not-allowed",
    },
    "& .MuiSvgIcon-root": {
      fontSize: 20,
    },
  },
}));

const toneClass = {
  ok: "pillOk",
  success: "pillOk",
  warning: "pillWarning",
  error: "pillError",
  danger: "pillError",
  info: "pillInfo",
};

export default function ModalHeader({
  id,
  title,
  subtitle,
  subtitleParts,
  pills,
  rightContent,
  onClose,
  closeDisabled,
  closeLabel,
  clientColor: clientColorProp,
  variant,
}) {
  const { clientColor } = useThemeColors();
  const classes = useStyles({
    clientColor: clientColorProp || clientColor,
    variant,
  });

  const parts = subtitleParts && subtitleParts.length ? subtitleParts : null;

  return (
    <DialogTitle id={id} disableTypography className={classes.root}>
      <div className={classes.strip} />
      <div className={classes.main}>
        <h3
          className={classes.title}
          title={typeof title === "string" ? title : undefined}
        >
          {title}
        </h3>
        {parts ? (
          <div className={classes.subtitle}>
            {parts.map((part, index) => (
              <React.Fragment key={index}>
                {index > 0 && <span className={classes.sep} />}
                <span>{part}</span>
              </React.Fragment>
            ))}
          </div>
        ) : subtitle ? (
          <div className={classes.subtitle}>
            <span>{subtitle}</span>
          </div>
        ) : null}
      </div>
      {(pills?.length || rightContent) && (
        <div className={classes.controls}>
          {pills?.map((pill, index) => {
            const tone = toneClass[pill.tone] || null;
            const className = [
              classes.pill,
              pill.dot ? classes.pillDot : null,
              tone ? classes[tone] : null,
            ]
              .filter(Boolean)
              .join(" ");
            return (
              <span
                className={className}
                title={pill.title}
                key={pill.key || index}
              >
                {pill.label}
              </span>
            );
          })}
          {rightContent}
        </div>
      )}
      {onClose && (
        <button
          type="button"
          className={classes.closeButton}
          aria-label={closeLabel}
          onClick={onClose}
          disabled={closeDisabled}
        >
          <Close />
        </button>
      )}
    </DialogTitle>
  );
}

ModalHeader.defaultProps = {
  closeLabel: "Close",
  variant: "dialog",
};

ModalHeader.propTypes = {
  id: PropTypes.string,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  subtitleParts: PropTypes.arrayOf(PropTypes.node),
  pills: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.node.isRequired,
      tone: PropTypes.oneOf([
        "neutral",
        "ok",
        "success",
        "warning",
        "error",
        "danger",
        "info",
      ]),
      dot: PropTypes.bool,
      title: PropTypes.string,
    }),
  ),
  rightContent: PropTypes.node,
  onClose: PropTypes.func,
  closeDisabled: PropTypes.bool,
  closeLabel: PropTypes.string,
  clientColor: PropTypes.string,
  variant: PropTypes.oneOf(["dialog", "drawer"]),
};
